// src/plugins/sales-forecast/components/HeaderBar.tsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import neighborhoodList from "../neighborhoods.json";
import neighborhoodGroupList from "../neighborhood_groups.json";
import marketList from "../markets.json";
import schoolDistrictList from "../school_districts.json";
import { LightDarkModeSwitch, Theme } from "./LightDarkModeSwitch";
import type { CloneEntry } from "./CloneNeighborhoodControl";

type CloneType = CloneEntry["cloneType"];
type StatusType = "Active" | "Developing" | "Archived";

interface HeaderBarProps {
  onCloneSelected: (entry: CloneEntry) => void;
  onForecastClick: () => void;
  onEditInputsClick: () => void;
  editInputsOpen: boolean;
  theme: Theme;
  onToggleTheme: () => void;
  lastRefreshed: Date | null;
}

function formatTimestamp(d: Date): string {
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

const selectStyle = (textColor: string, borderColor: string, inputBg: string): React.CSSProperties => ({
  backgroundColor: inputBg,
  color: textColor,
  border: `1px solid ${borderColor}`,
  borderRadius: "8px",
  padding: "0.45rem 0.65rem",
  fontSize: "0.85rem",
  fontWeight: 500,
  cursor: "pointer",
  minWidth: 0,
});

export const HeaderBar: React.FC<HeaderBarProps> = ({
  onCloneSelected,
  onForecastClick,
  onEditInputsClick,
  editInputsOpen,
  theme,
  onToggleTheme,
  lastRefreshed,
}) => {
  const today = new Date().toISOString().split("T")[0];

  const [cloneType, setCloneType] = useState<CloneType>("Neighborhood");
  const [status, setStatus] = useState<StatusType>("Active");
  const [allEntries, setAllEntries] = useState<CloneEntry[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<string>(today);

  const statusFilterApplies = cloneType === "Neighborhood" || cloneType === "Neighborhood - Group";

  const buildList = useCallback(
    (source: any[], type: CloneType): CloneEntry[] =>
      (source || []).map((n) => ({
        id: n.id,
        title: n.title,
        cloneType: type,
        status: n.status,
        defaults: {
          ...n.defaults,
          amenities: Array.isArray(n?.defaults?.amenities) ? [...n.defaults.amenities] : [],
          cloneDate: selectedDate,
        },
      })),
    [selectedDate]
  );

  useEffect(() => {
    let sourceList: any[] = [];
    if (cloneType === "Neighborhood") sourceList = neighborhoodList as any[];
    else if (cloneType === "Neighborhood - Group") sourceList = neighborhoodGroupList as any[];
    else if (cloneType === "Market") sourceList = marketList as any[];
    else if (cloneType === "School District") sourceList = schoolDistrictList as any[];
    setAllEntries(buildList(sourceList, cloneType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloneType]);

  const entries = useMemo(() => {
    if (!statusFilterApplies) return allEntries;
    return allEntries.filter((e) => (e.status ?? "").toLowerCase() === status.toLowerCase());
  }, [allEntries, status, statusFilterApplies]);

  useEffect(() => {
    const firstId = entries.length ? entries[0].id : "";
    setSelectedId((prev) => (entries.some((e) => e.id === prev) ? prev : firstId));
    const entry = entries.find((e) => e.id === selectedId) || entries[0] || null;
    if (entry) {
      requestAnimationFrame(() =>
        onCloneSelected({
          ...entry,
          status,
          defaults: { ...entry.defaults, cloneDate: selectedDate },
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, status]);

  const handleCloneChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newId = e.target.value;
    setSelectedId(newId);
    const found = entries.find((entry) => entry.id === newId);
    if (found) {
      onCloneSelected({ ...found, status, defaults: { ...found.defaults, cloneDate: selectedDate } });
    }
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value as StatusType;
    setStatus(next);
    const found = entries.find((entry) => entry.id === selectedId) || entries[0] || null;
    if (found) {
      onCloneSelected({ ...found, status: next, defaults: { ...found.defaults, cloneDate: selectedDate } });
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = e.target.value;
    setSelectedDate(date);
    const found = entries.find((entry) => entry.id === selectedId);
    if (found) {
      onCloneSelected({ ...found, status, defaults: { ...found.defaults, cloneDate: date } });
    }
  };

  const textColor = "var(--text-color)";
  const mutedText = "var(--muted-text-color)";
  const borderColor = "var(--border-color)";
  const inputBg = "var(--input-bg)";
  const accentColor = "var(--accent-color)";
  const cardBg = "var(--card-bg)";

  const selLabel =
    cloneType === "Neighborhood"
      ? "Neighborhood"
      : cloneType === "Neighborhood - Group"
        ? "Group"
        : cloneType === "Market"
          ? "Market"
          : "School District";

  return (
    <header
      style={{
        backgroundColor: cardBg,
        borderBottom: `1px solid ${borderColor}`,
        padding: "0.75rem 1.25rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.6rem",
        position: "sticky",
        top: 0,
        zIndex: 50,
      }}
    >
      {/* ── Title row ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem" }}>
        <h1
          style={{
            margin: 0,
            fontSize: "1.6rem",
            fontWeight: 800,
            letterSpacing: "0.08em",
            color: textColor,
            fontFamily: "'Montserrat', sans-serif",
            whiteSpace: "nowrap",
          }}
        >
          MARKET FORECAST
        </h1>

        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexShrink: 0 }}>
          {lastRefreshed && (
            <span style={{ fontSize: "0.75rem", color: mutedText, whiteSpace: "nowrap" }}>
              Updated {formatTimestamp(lastRefreshed)}
            </span>
          )}
          <LightDarkModeSwitch theme={theme} onToggle={onToggleTheme} />
        </div>
      </div>

      {/* ── Control row ── */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          gap: "0.5rem",
        }}
      >
        {/* Type */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem", minWidth: "120px" }}>
          <label style={{ fontSize: "0.7rem", fontWeight: 600, color: mutedText, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Type
          </label>
          <select value={cloneType} onChange={(e) => setCloneType(e.target.value as CloneType)} style={selectStyle(textColor, borderColor, inputBg)}>
            <option value="Neighborhood">Neighborhood</option>
            <option value="Neighborhood - Group">Neighborhood Group</option>
            <option value="Market">Market</option>
            <option value="School District">School District</option>
          </select>
        </div>

        {/* Status — only when relevant */}
        {statusFilterApplies && (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem", minWidth: "110px" }}>
            <label style={{ fontSize: "0.7rem", fontWeight: 600, color: mutedText, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Status
            </label>
            <select value={status} onChange={handleStatusChange} style={selectStyle(textColor, borderColor, inputBg)}>
              <option value="Active">Active</option>
              <option value="Developing">Developing</option>
              <option value="Archived">Archived</option>
            </select>
          </div>
        )}

        {/* Entity selector */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem", minWidth: "180px", flex: "1 1 180px", maxWidth: "320px" }}>
          <label style={{ fontSize: "0.7rem", fontWeight: 600, color: mutedText, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {selLabel}
          </label>
          <select value={selectedId} onChange={handleCloneChange} style={selectStyle(textColor, borderColor, inputBg)}>
            {entries.map((e) => (
              <option key={e.id} value={e.id}>{e.title}</option>
            ))}
          </select>
        </div>

        {/* Forecast Start Date */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem", minWidth: "150px" }}>
          <label style={{ fontSize: "0.7rem", fontWeight: 600, color: mutedText, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Forecast Start Date
          </label>
          <input
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            style={{ ...selectStyle(textColor, borderColor, inputBg), cursor: "pointer" }}
          />
        </div>

        {/* Spacer */}
        <div style={{ flex: "1 1 0" }} />

        {/* Edit Inputs button */}
        <button
          type="button"
          onClick={onEditInputsClick}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.3rem",
            padding: "0.45rem 0.9rem",
            borderRadius: "8px",
            border: `1px solid ${borderColor}`,
            backgroundColor: editInputsOpen ? accentColor : inputBg,
            color: editInputsOpen ? "#fff" : textColor,
            fontSize: "0.85rem",
            fontWeight: 600,
            cursor: "pointer",
            whiteSpace: "nowrap",
            transition: "background-color 0.15s ease, color 0.15s ease",
            alignSelf: "flex-end",
          }}
        >
          {editInputsOpen ? "Close Inputs" : "Edit Inputs"}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: editInputsOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s ease" }}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        {/* Forecast button */}
        <button
          type="button"
          onClick={onForecastClick}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.4rem",
            padding: "0.45rem 1.1rem",
            borderRadius: "8px",
            border: "none",
            backgroundColor: accentColor,
            color: "#fff",
            fontSize: "0.85rem",
            fontWeight: 700,
            cursor: "pointer",
            whiteSpace: "nowrap",
            letterSpacing: "0.03em",
            alignSelf: "flex-end",
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
          </svg>
          Forecast
        </button>
      </div>
    </header>
  );
};
